/* eslint-disable @typescript-eslint/no-explicit-any */
import 'dayjs/locale/pt'

import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import type { UseFormReturn } from 'react-hook-form'

import { Button } from '@/modules/shared/components/ui/button'
import { Calendar } from '@/modules/shared/components/ui/calendar'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/modules/shared/components/ui/form'
import { Input } from '@/modules/shared/components/ui/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/modules/shared/components/ui/popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/modules/shared/components/ui/select'
import { cn } from '@/modules/shared/lib/utils'

import type { FilterFormValues } from '../../hooks/use-filter-orders.hook'

type FilterFormPeriodProps = {
	form: UseFormReturn<FilterFormValues>
	handlePressEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export function OrderFilterFormPeriodComponent({
	form,
	handlePressEnter,
}: FilterFormPeriodProps) {
	const [date, setDate] = useState<DateRange | undefined>({
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	})

	useEffect(() => {
		if (date?.to && date?.from) {
			form.setValue('dataInicio', format(date.from, 'yyyy-MM-dd'))
			form.setValue('dataFim', format(date.to, 'yyyy-MM-dd'))
		}
	}, [date])

	return (
		<div className="space-y-3 pt-3">
			<div className={cn('grid space-y-3')}>
				<FormLabel htmlFor="cliente">Selecione um período</FormLabel>
				<Popover>
					<PopoverTrigger asChild>
						<Button
							id="date"
							variant={'outline'}
							className={cn(
								'max-w-full justify-start text-foreground text-left font-normal',
								!date && 'text-muted-foreground',
							)}
						>
							<CalendarIcon className="mr-2 h-4 w-4" />
							{date?.from ? (
								date.to ? (
									<>
										{dayjs(date.from).locale('pt-br').format('DD/MM/YYYY')} -{' '}
										{dayjs(date.to).locale('pt-br').format('DD/MM/YYYY')}
									</>
								) : (
									dayjs.utc(date.from).locale('pt-br').format('DD/MM/YYYY')
								)
							) : (
								<span>Escolha um período</span>
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent
						className="w-full max-h-50vh text-foreground"
						align="start"
					>
						<Calendar
							locale={ptBR}
							initialFocus
							mode="range"
							defaultMonth={date?.from}
							selected={date}
							onSelect={setDate}
							numberOfMonths={1}
						/>
					</PopoverContent>
				</Popover>
			</div>

			<FormField
				control={form.control}
				name="cliente"
				render={({ field }) => (
					<FormItem>
						<FormLabel htmlFor="cliente">Cliente</FormLabel>
						<FormControl>
							<Input
								{...field}
								id="cliente"
								placeholder="Nome do cliente"
								onClick={(e) => {
									const inputElement = e.target as HTMLInputElement
									inputElement.select()
								}}
								onKeyUp={handlePressEnter}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name="cnpjcpf"
				render={({ field }) => (
					<FormItem>
						<FormLabel htmlFor="cnpjcpf">CNPJ/CPF</FormLabel>
						<FormControl>
							<Input
								{...field}
								id="cnpjcpf"
								placeholder="CNPJ/CPF"
								onClick={(e) => {
									const inputElement = e.target as HTMLInputElement
									inputElement.select()
								}}
								onKeyUp={handlePressEnter}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name="status"
				render={({ field }) => (
					<FormItem>
						<FormLabel htmlFor="status">Status</FormLabel>
						<Select onValueChange={field.onChange} defaultValue={field.value}>
							<FormControl>
								<SelectTrigger>
									<SelectValue id="status" placeholder="Selecione um status" />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								<SelectItem value="AT">AT - Aguardando Importação</SelectItem>
								<SelectItem value="TR">TR - Importado</SelectItem>
								<SelectItem value="RA">RA - Rascunho</SelectItem>
								<SelectItem value="AP">AP - Bloqueado</SelectItem>
								<SelectItem value="CA">CA - Carregado</SelectItem>
								<SelectItem value="CN">CN - Cancelado</SelectItem>
								<SelectItem value="LI">LI - Liberado</SelectItem>
								<SelectItem value="LP">LP - Liberado Parcialmente</SelectItem>
								<SelectItem value="PE">PE - Pendente</SelectItem>
							</SelectContent>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}
